import React from 'react'
import { imagePropsToClassNames } from './helpers'
import type { ResponsiveImageProp } from '../shared'
import tailwindConfig from '../../../../tailwind.config'
import { IMAGE_BREAKPOINTS } from '../shared'

const IMAGE_BREAKPOINTS_REVERSED = IMAGE_BREAKPOINTS.toReversed()

const screenBreakpoints = tailwindConfig.theme.screens

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    /**
     * Additional class(es) to be applied to the <img> tag
     */
    className?: string
    /**
     * Optional class to be added to the <picture> tag that wraps the <img> tag.
     */
    pictureTagClassName?: string
    /**
     * URLs used as sources for the <picture> and <img> elements.
     * `base` breakpoint is required; all other breakpoints are optional.
     */
    responsiveUrls: ResponsiveImageProp<string>
    /**
     * Alt text for the image. Marked as optional since this is a lower-level component that
     * just wraps <picture> and <img>, but there really should always be alt text for accessibility
     */
    alt?: string
    /**
     * If true, the image will be cropped into a circle (border-radius 100%)
     */
    ellipse?: boolean
    /**
     * Sets the object-fit CSS property on the <img> element. @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
     * @default 'contain'
     *
     * Note: object-fit classes are built dynamically, which works thanks to `tailwind-safelist.md`.
     */
    objectFit?: 'contain' | 'cover' | 'fill' | 'scale-down' | 'none'
    /**
     * Sets the object-position CSS property on the <img> element. @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-position
     * @default 'center'
     *
     * Note: object-position classes are built dynamically, which works thanks to `tailwind-safelist.md`.
     */
    objectPosition?:
        | 'bottom'
        | 'center'
        | 'left'
        | 'left-bottom'
        | 'left-top'
        | 'right'
        | 'right-bottom'
        | 'right-top'
        | 'top'
    /**
     * HTML `style` attribute
     */
    style?: Record<string, string>
    ref?: React.Ref<HTMLImageElement>
    onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void
}

export function Image({
    className,
    pictureTagClassName,
    alt,
    title,
    responsiveUrls,
    objectFit = 'scale-down',
    objectPosition = 'center',
    ellipse,
    ref,
    onLoad,
    ...additionalHtmlProps
}: Readonly<ImageProps>) {
    const imgClassNames = imagePropsToClassNames({
        className,
        ellipse,
        objectFit,
        objectPosition,
    })
    const { base, ...urlsForTabletAndUp } = responsiveUrls

    // Larger <source>s must come first, so loop over the *reversed* breakpoints array
    const sources = IMAGE_BREAKPOINTS_REVERSED.map((breakpoint) => {
        const url =
            urlsForTabletAndUp[breakpoint as keyof typeof urlsForTabletAndUp]
        return (
            url && (
                <source
                    key={breakpoint}
                    media={`(min-width: ${
                        screenBreakpoints[
                            breakpoint as keyof typeof screenBreakpoints
                        ]
                    })`}
                    srcSet={url}
                />
            )
        )
    })

    return (
        <picture className={pictureTagClassName}>
            {sources}
            <img
                ref={ref}
                className={imgClassNames.join(' ')}
                src={responsiveUrls.base}
                alt={alt ?? ''}
                title={title}
                onLoad={onLoad}
                {...additionalHtmlProps}
            />
        </picture>
    )
}
